// src/components/Chat/ChatLoading.jsx
import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Typography, CircularProgress } from '@mui/material';
import { useCategory } from '../../contexts/CategoryContext';
import api from '../../api/axios';
import EnterPasswordDialog from './EnterPasswordDialog';
import ChatRoomView from './ChatRoomView';

const ChatLoading = () => {
    const { categoryId } = useParams();
    const currentId = Number(categoryId);
    const navigate = useNavigate();
    const { chatCategories } = useCategory();

    const [loading, setLoading] = useState(true);
    const [category, setCategory] = useState(null);
    const [nickname, setNickname] = useState('');
    const [needPwd, setNeedPwd] = useState(false);
    const [error, setError] = useState(null);

    // categoryId 바뀌면 상태 초기화
    useEffect(() => {
        setLoading(true);
        setCategory(null);
        setNickname('');
        setNeedPwd(false);
        setError(null);
    }, [categoryId]);

    // /enter 호출 (useCallback으로 안정성 확보)
    const enterRoom = useCallback(async (password = '') => {
        try {
            const saved = sessionStorage.getItem(`nickname_${categoryId}`);
            const res = await api.post(
                `/api/chat/categories/${categoryId}/enter/`,
                password ? { password } : {}
            );
            const nick = saved || res.data.nickname || '익명';
            setNickname(nick);
            if (!saved) sessionStorage.setItem(`nickname_${categoryId}`, nick);
            setLoading(false);
        } catch (err) {
            const msg = err.response?.data?.error || '알 수 없는 오류';
            if (msg.includes('비밀번호')) {
                setNeedPwd(true);
                return;
            }
            setError(`입장 실패: ${msg}`);
            setLoading(false);
        }
    }, [categoryId]);

    // 메인 로직
    useEffect(() => {
        if (!chatCategories || chatCategories.length === 0) return;

        const found = chatCategories.find(c => c.id === currentId);
        if (!found) {
            setError('해당 채팅방을 찾을 수 없습니다.');
            setLoading(false);
            return;
        }
        setCategory(found);

        if (found.is_private_only) {
            setLoading(false);
            setNeedPwd(true);
            return;
        }

        enterRoom(); // 공개방은 바로 입장
    }, [chatCategories, categoryId, currentId, enterRoom]);

    // 비번 모달 콜백
    const handlePwdSuccess = (newNick, password) => {
        sessionStorage.setItem(`nickname_${categoryId}`, newNick);
        setNickname(newNick);
        setNeedPwd(false);
        setLoading(true);
        enterRoom(password);
    };

    const handlePwdCancel = () => navigate('/main/chat');

    // 렌더링
    if (needPwd)
        return (
            <EnterPasswordDialog
                open
                categoryId={categoryId}
                onClose={handlePwdCancel}
                onCancel={handlePwdCancel}
                onSuccess={handlePwdSuccess}
            />
        );

    if (!chatCategories || loading)
        return <CircularProgress sx={{ m: 4 }} />;

    if (error)
        return <Typography sx={{ p: 4 }} color="error">{error}</Typography>;

    return (
        <ChatRoomView
            category={category}
            categoryId={categoryId}
            nickname={nickname}
        />
    );
};

export default ChatLoading;
